import React, { createContext, useContext, useState, useCallback } from "react";
import type { LifeSelection } from "./types";

interface LifeSelectionContextValue {
  selections: LifeSelection[];
  addSelection: (sel: LifeSelection) => void;
  removeSelection: (itemId: string) => void;
  clearSelections: () => void;
}

const LifeSelectionContext = createContext<LifeSelectionContextValue>({
  selections: [],
  addSelection: () => {},
  removeSelection: () => {},
  clearSelections: () => {},
});

export function LifeSelectionProvider({ children }: { children: React.ReactNode }) {
  const [selections, setSelections] = useState<LifeSelection[]>([]);

  const addSelection = useCallback((sel: LifeSelection) => {
    setSelections((prev) => {
      // Don't duplicate
      if (prev.some((s) => s.itemId === sel.itemId)) return prev;
      return [...prev, sel];
    });
  }, []);

  const removeSelection = useCallback((itemId: string) => {
    setSelections((prev) => prev.filter((s) => s.itemId !== itemId));
  }, []);

  const clearSelections = useCallback(() => {
    setSelections([]);
  }, []);

  return (
    <LifeSelectionContext.Provider
      value={{ selections, addSelection, removeSelection, clearSelections }}
    >
      {children}
    </LifeSelectionContext.Provider>
  );
}

export function useLifeSelection() {
  return useContext(LifeSelectionContext);
}
